import moment from 'moment-timezone'

import JsonViewer from 'vue-json-viewer'

import {
  getShortTransactionLogId,
  formatCurrency,
  formatDate,
  formatTime,
  generateKeywordsQueryForOr,
  adminAgentCreditRefundPermitted,
  getRoleNames
} from '@smarttransit/common'

import {
  addParamsToLocation
} from '@smarttransit/common-client'

import { genericApiRequests } from '@/utilities/axios-factory'
import { addAlert, isAuthorized, isOnlyInvestor as isOnlyInvestorFn } from '@/utilities/helpers'

import {
  findTotalVendorTransactionLogs,
  findVendorTransactionLogs,
  findTotalGeneralVendorTransactionLogs,
  findGeneralVendorTransactionLogs,
  expireTicketNow,
  refundPurchasedBeacon
} from '@/services/passenger-transaction-logs-service'

import {
  createUnusedBalanceRefund,
  createAgentCreditRefund
  // deleteUnusedBalanceRefund
} from '@/services/passengers-service'

import { getUser } from '@/services/users-service'
import { getAgentTransactionLog } from '@/services/agent-transaction-logs-service'
import { findPassengerTrips } from '@/services/passenger-trips-service'
import { getBusStopsByRouteIdList } from '@/services/bus-stops-service'
import { findStRoutes } from '@/services/st-routes-service'

import {
  buildTripPopupHtml,
  fitMapBounds,
  getRouteCoordinatesDistance,
  formatDistanceLabel,
  formatTripOptionLabel,
  getTripBusStopRouteIdList,
  getTripFareAmount,
  getTripLicencePlate,
  getUniqueTripLicencePlates,
  normalizeGeoPoint,
  routeGeometryToCoordinates,
  toMapboxBusStops,
  TRIP_BUS_STOPS_MIN_ZOOM
} from './trip-map'

import {
  isAgentTopUpPassengerTransaction,
  getAgentTopUpTransactionLogId,
  getAgentStUserFromJoinedLog,
  shouldShowDateRecorded,
  getRelatedTransactionUserActions,
  getEventDetailRows,
  getPersonDetailRows,
  toViewedPerson,
  getReconfirmCodeLabel,
  formatTripRouteLabel,
  getTicketSurchargeRows,
  getRefundSurchargeHint,
  getTicketFareFeeLabel,
  getExpireNowSurchargeHint,
  getTicketSurchargeAmount
} from './passenger-transaction-display'

const TRIP_ENDPOINTS_SOURCE_ID = 'passenger-trip-endpoints'
const TRIP_ENDPOINTS_LAYER_ID = 'passenger-trip-endpoints-layer'
const TRIP_BOARD_ICON_ID = 'passenger-trip-board-icon'
const TRIP_ALIGHT_ICON_ID = 'passenger-trip-alight-icon'
const TRIP_BOARD_ICON_URL = `${location.origin}/images/map-marker-start.png`
const TRIP_ALIGHT_ICON_URL = `${location.origin}/images/map-marker-end.png`
// Pin PNGs are 512px; ~0.09 renders them ~46px tall, matching the old marker size.
const TRIP_ENDPOINT_ICON_SIZE = 0.09

export default {
  name: 'passenger-transactions',
  components: {
    JsonViewer
  },
  props: {
    signedInUser: Object,
    forceRootViewRefresh: Function
  },
  data () {
    return {
      apiInProgress: false,
      countInProgress: false,
      lastSearchRequestId: 0,
      searchKeywords: '',
      searchPhone: '',
      selectedVendor: '',
      metadataLoaded: false,
      metadataTab: 'details',
      selectedMetadata: null,
      expireNowRefundUnusedBalance: false,
      countryCurrency: null,
      dateFrom: null,
      dateTo: null,
      headers: [],
      transactions: [],
      // When null, total is unknown (use next/previous pagination only)
      totalTransactions: null,
      createUnusedBalanceRefundDialog: false,
      newUnusedBalanceRefund: {},
      createAgentCreditRefundDialog: false,
      newAgentCreditRefund: {},
      transactionTrips: [],
      transactionTripsLoading: false,
      transactionTripsError: '',
      selectedTripId: null,
      tripBusStops: [],
      tripBusStopsLoading: false,
      tripMapHeightOffset: 270,
      tripMapActivated: false,
      tripMapboxInstanceWrapper: null,
      tripMapPopups: [],
      tripBoardIconUrl: TRIP_BOARD_ICON_URL,
      tripAlightIconUrl: TRIP_ALIGHT_ICON_URL,
      stMapApis: {
        genericApiRequests,
        addAlert
      },
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_KEY,
      maptilerKey: process.env.VUE_APP_MAPTILER_KEY,
      markerOptions: { startMarkerClass: 'st-map-marker-start', endMarkerClass: 'st-map-marker-end' },
      logTypes: [
        { text: 'Purchased/One time Ticket', value: 'purchased_ticket' },
        { text: 'Purchased Credit', value: 'purchased_credit' },
        { text: 'Purchased Gift', value: 'purchased_gift' },
        { text: 'Refund', value: 'refund' },
        { text: 'Unused Balance Refund', value: 'unused_balance_refund' },
        { text: 'Purchased Beacon', value: 'purchased_beacon' },
        { text: 'Purchased Beacon Refund', value: 'purchased_beacon_refund' }
      ],
      selectedLogTypes: [],
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'dateUpdated'
      },
      isVendor: false,
      vendors: [ { text: 'MTN Ghana', value: 'mtnghana', metadata: { countryId: 'GHA' } }, { text: 'Airtel/Tigo Ghana', value: 'atigoghana', metadata: { countryId: 'GHA' } }, { text: 'Telecel Ghana', value: 'vodaghana', metadata: { countryId: 'GHA' } } ]
    }
  },
  computed: {
    isAdmin () {
      return isAuthorized(this.$props.signedInUser, 'admin')
    },
    isOnlyInvestor () {
      return isOnlyInvestorFn(this.$props.signedInUser)
    },
    selectedAgentBlocksAdminRefund () {
      const agent = this.newAgentCreditRefund && this.newAgentCreditRefund.agent
      if (!agent) {
        return false
      }
      return !adminAgentCreditRefundPermitted({
        roleNames: getRoleNames(agent.stUser),
        requiresAgentCredit: agent.requiresAgentCredit
      })
    },
    isTotalTransactionsKnown () {
      return typeof this.totalTransactions === 'number'
    },
    tableTotalItems () {
      // Vuetify's v-data-table clamps `pagination.page` based on `total-items`.
      // When totals are unknown (we use custom next/prev), we must still provide a
      // non-zero "server items length" so the table doesn't reset back to page 1.
      if (this.isTotalTransactionsKnown) {
        return this.totalTransactions
      }

      const rowsPerPage = Number(this.pagination && this.pagination.rowsPerPage) || 10
      const page = Number(this.pagination && this.pagination.page) || 1
      return page * rowsPerPage
    },
    canGoToPreviousPage () {
      return this.pagination.page > 1 && !this.apiInProgress
    },
    canGoToNextPage () {
      // With unknown totals, the only safe heuristic is: if we got a full page, assume there may be more.
      return this.transactions && this.transactions.length === this.pagination.rowsPerPage && !this.apiInProgress
    },
    knownPageCount () {
      if (!this.isTotalTransactionsKnown) return 0
      const rowsPerPage = Number(this.pagination.rowsPerPage) || 10
      const total = Number(this.totalTransactions) || 0
      return Math.max(1, Math.ceil(total / rowsPerPage))
    },
    selectedPurchasedTickets () {
      return this.transactions.filter((o) => o.selected)
    },
    selectedPurchasedTicketsTotalLabel () {
      if (!this.selectedPurchasedTickets.length || !this.countryCurrency) return ''

      const total = this.selectedPurchasedTickets.reduce((sum, ticket) => {
        return sum + (Number(ticket.amount) || 0)
      }, 0)
      const amountClass = total < 0 ? 'error--text' : ''

      return `<span class="${amountClass}">${this.countryCurrency.currencySymbol}${formatCurrency(total)}</span>`
    },
    refundSurchargeHint () {
      return getRefundSurchargeHint(this.selectedPurchasedTickets, (cents) => this.formatSurchargeAmount(cents))
    },
    expireNowSurchargeHint () {
      return getExpireNowSurchargeHint(this.selectedMetadata)
    },
    refundPassengerPhoneLabel () {
      const selected = this.selectedPurchasedTickets
      if (!selected.length) return ''

      const firstTicket = selected[0]
      if (firstTicket.phoneLabel) return firstTicket.phoneLabel
      if (firstTicket.passengerIdentifier && firstTicket.passengerIdentifier.phoneNumber) {
        return firstTicket.passengerIdentifier.phoneNumber
      }
      return ''
    },
    isSelectedPurchasedTicket () {
      return this.selectedMetadata && this.selectedMetadata.logType === 'purchased_ticket'
    },
    canExpireSelectedTicket () {
      if (!this.isAdmin || this.isOnlyInvestor || !this.selectedMetadata) return false
      if (this.selectedMetadata.logType !== 'purchased_ticket') return false
      if (this.selectedMetadata.logStatus === 'expired') return false
      if (this.selectedMetadata.logStatus === 'completed') return false
      return true
    },
    canRefundPurchasedBeacon () {
      if (!this.isAdmin || this.isOnlyInvestor) return false
      if (!this.selectedMetadata || this.selectedMetadata.logType !== 'purchased_beacon') return false
      const logMetadata = this.selectedMetadata.logMetadata || {}
      return !logMetadata.purchasedBeaconRefundId
    },
    selectedPurchasedBeaconIdentifierKey () {
      if (!this.selectedMetadata || !this.selectedMetadata.logMetadata) return ''
      return this.selectedMetadata.logMetadata.identifierKey || ''
    },
    selectedPurchasedBeaconRefundAmountLabel () {
      if (!this.selectedMetadata || !this.countryCurrency) return ''
      return `${this.countryCurrency.currencySymbol}${formatCurrency(Math.abs(this.selectedMetadata.amount || 0))}`
    },
    selectedTrip () {
      if (!this.selectedTripId) return null
      return this.transactionTrips.find((trip) => trip.id === this.selectedTripId) || null
    },
    selectedTripRouteCoordinates () {
      return this.selectedTrip ? routeGeometryToCoordinates(this.selectedTrip.routeGeometry) : []
    },
    tripMapDefaultCenter () {
      // Start the map at the trip instead of the default world view. Creating it at
      // [0,0]/zoom 0 and then jumping to the route shows massively scaled-up low-zoom
      // tiles until the real tiles load, which reads as a smear on the first paint.
      const board = normalizeGeoPoint(this.selectedTrip ? this.selectedTrip.longlatEmbarked : null)
      if (board) return [board.lng, board.lat]
      const coordinates = this.selectedTripRouteCoordinates
      if (coordinates.length) return coordinates[0]
      return null
    },
    shouldRenderTripMap () {
      return Boolean(
        this.metadataLoaded &&
        this.tripMapActivated &&
        this.selectedTrip &&
        this.stMapApis &&
        this.mapboxAccessToken &&
        this.maptilerKey
      )
    },
    selectedTripOptions () {
      return this.transactionTrips.map((trip, index) => ({
        text: trip.routeLabel
          ? `Trip ${index + 1}: ${trip.routeLabel}`
          : formatTripOptionLabel(trip, index),
        value: trip.id
      }))
    },
    selectedTripLicencePlate () {
      return this.selectedTrip ? getTripLicencePlate(this.selectedTrip) : ''
    },
    selectedTicketLicencePlateLabel () {
      const licencePlates = getUniqueTripLicencePlates(this.transactionTrips)
      return licencePlates.length ? licencePlates.join(', ') : ''
    },
    selectedTripFareLabel () {
      if (!this.selectedTrip || !this.countryCurrency) return 'N/A'
      const fareAmount = getTripFareAmount({
        transaction: this.selectedMetadata,
        trip: this.selectedTrip,
        trips: this.transactionTrips
      })
      return `${this.countryCurrency.currencySymbol}${formatCurrency(fareAmount)}`
    },
    selectedTripDistanceLabel () {
      if (!this.selectedTrip) return 'N/A'
      const distance = this.selectedTrip.finalDistanceTravelled || getRouteCoordinatesDistance(this.selectedTripRouteCoordinates)
      return formatDistanceLabel(distance)
    },
    selectedTripRouteLabel () {
      return (this.selectedTrip && this.selectedTrip.routeLabel) || 'N/A'
    },
    selectedTripInfoRows () {
      if (!this.selectedTrip) return []
      return [
        { label: 'Route', value: this.selectedTripRouteLabel },
        { label: 'Bus licence plate', value: this.selectedTripLicencePlate || 'N/A' },
        { label: 'Final fare', value: this.selectedTripFareLabel },
        { label: 'Distance travelled', value: this.selectedTripDistanceLabel }
      ]
    },
    tripMapContainerId () {
      const suffix = this.selectedMetadata && this.selectedMetadata.id ? this.selectedMetadata.id : 'empty'
      return `passengerTransactionTripMap-${suffix}`
    },
    showDateRecorded () {
      return shouldShowDateRecorded(this.selectedMetadata)
    },
    transactionInfoRows () {
      if (!this.selectedMetadata) return []
      const rows = [
        { label: 'ID', value: this.selectedMetadata.parsedId },
        {
          label: 'Type',
          value: this.selectedMetadata.logTypeLabel || this.selectedMetadata.logType,
          icon: this.selectedMetadata.logTypeIcon
        },
        {
          label: 'Status',
          value: this.selectedMetadata.logStatus,
          icon: this.selectedMetadata.logStatusIcon
        }
      ]
      const reconfirmCode = getReconfirmCodeLabel(this.selectedMetadata.logMetadata)
      if (reconfirmCode) {
        rows.push({ label: 'Reconfirm code', value: reconfirmCode })
      }
      if (this.isSelectedPurchasedTicket) {
        rows.push({
          label: 'Bus licence plate(s)',
          value: this.selectedTicketLicencePlateDisplay
        })
        const surchargeRows = getTicketSurchargeRows(this.selectedMetadata, (cents) => this.formatSurchargeAmount(cents))
        for (let i = 0; i < surchargeRows.length; i++) {
          rows.push(surchargeRows[i])
        }
      }
      return rows
    },
    selectedTicketLicencePlateDisplay () {
      if (this.transactionTripsLoading) return 'Loading...'
      return this.selectedTicketLicencePlateLabel || 'N/A'
    },
    dateInfoRows () {
      if (!this.selectedMetadata) return []
      const rows = [
        { label: 'Created', value: this.selectedMetadata.dateCreatedLabel }
      ]
      if (this.showDateRecorded) {
        rows.push({ label: 'Recorded', value: this.selectedMetadata.dateRecordedLabel })
      }
      rows.push({ label: 'Updated', value: this.selectedMetadata.dateUpdatedLabel })
      return rows
    },
    eventDetailRows () {
      return getEventDetailRows(this.selectedMetadata && this.selectedMetadata.cachedTransportationProfileEvent)
    },
    relatedUserActions () {
      if (!this.isAdmin) return []
      return getRelatedTransactionUserActions(this.selectedMetadata && this.selectedMetadata.logMetadata)
    },
    viewedUserRows () {
      return getPersonDetailRows(this.selectedMetadata && this.selectedMetadata.viewedUser)
    },
    viewedAgentRows () {
      return getPersonDetailRows(this.selectedMetadata && this.selectedMetadata.viewedAgent)
    },
    hasPeopleDetails () {
      return Boolean(
        this.relatedUserActions.length ||
        this.viewedUserRows.length ||
        this.viewedAgentRows.length ||
        (this.selectedMetadata && this.selectedMetadata.isAgentTopUp)
      )
    },
    hasLogMetadata () {
      const metadata = this.selectedMetadata && this.selectedMetadata.logMetadata
      return Boolean(metadata && typeof metadata === 'object' && Object.keys(metadata).length)
    },
    logMetadataValue () {
      if (!this.hasLogMetadata) return {}
      return this.selectedMetadata.logMetadata
    }
  },
  mounted () {
    this.headers = [
      ...(this.isAdmin && !this.isOnlyInvestor ? [{
        text: '',
        align: 'left',
        sortable: false
      }] : []),
      {
        text: 'Phone',
        align: 'left',
        sortable: false
      },
      // {
      //   text: 'Email',
      //   align: 'left',
      //   sortable: false
      // },
      {
        text: 'Amount',
        align: 'left',
        sortable: true,
        value: 'amount'
      },
      {
        text: 'Credit Before',
        align: 'left',
        sortable: true,
        value: 'profileBalanceBefore'
      },
      {
        text: 'After',
        align: 'left',
        sortable: true,
        value: 'profileBalanceAfter'
      },
      {
        text: 'Log Type',
        align: 'left',
        sortable: true,
        value: 'logType'
      },
      {
        text: 'Log Status',
        align: 'left',
        sortable: true,
        value: 'logStatus'
      },
      {
        text: 'Last Updated',
        align: 'left',
        sortable: true,
        value: 'dateUpdated',
        class: 'passenger-transaction-date-header',
        width: '120px'
      },
      {
        text: 'Date Created',
        align: 'left',
        sortable: true,
        value: 'dateRecorded',
        class: 'passenger-transaction-date-header',
        width: '120px'
      }
    ].filter(Boolean)

    this.loadFromQueryParams()

    if (this.$props.signedInUser) {
      this.isVendor = this.$props.signedInUser.roles.find((o) => (o.name === 'vendor'))

      if (this.isVendor) {
        const permittedVendorTypes = ['mtnghana'] // this.$props.signedInUser.metadata?.vendorTypes || []
        this.vendors = this.vendors.filter((o) => (permittedVendorTypes.includes(o.value)))

        if (this.vendors.length === 1) {
          this.selectedVendor = this.vendors[0].value
        }
      }

      this.initCountryCurrency().then(() => {
        return this.searchTransactions()
      }).then(() => {
        this.componentMounted = true
      })
    }
  },
  watch: {
    selectedVendor () {
      this.lastParams = addParamsToLocation({ selectedVendor: this.selectedVendor }, this.$route, this.lastParams)
      if (this.componentMounted) {
        this.pagination.page = 1
        this.searchTransactions()
      }
    },
    selectedLogTypes () {
      this.lastParams = addParamsToLocation({ selectedLogTypes: this.selectedLogTypes }, this.$route, this.lastParams)
    },
    pagination (updatedPagination) {
      this.lastParams = addParamsToLocation(updatedPagination, this.$route, this.lastParams)
    },
    metadataLoaded (isLoaded) {
      if (!isLoaded) {
        this.resetTransactionTripState()
      }
    },
    metadataTab (tab) {
      if (tab === 'trips' && !this.tripMapActivated) {
        this.activateTripMap()
      }
    },
    selectedTripId () {
      this.tripBusStops = []
      this.destroyTripMapState()
      if (this.tripMapActivated) {
        this.loadSelectedTripBusStops()
      }
    }
  },
  methods: {
    formatSurchargeAmount (cents) {
      const symbol = this.countryCurrency ? this.countryCurrency.currencySymbol : ''
      return `${symbol}${formatCurrency(cents)}`
    },
    ticketFareFeeLabel (ticket) {
      return getTicketFareFeeLabel(ticket, (cents) => this.formatSurchargeAmount(cents))
    },
    loadFromQueryParams () {
      if (this.$route.query.descending) {
        this.pagination.descending = this.$route.query.descending
      }
      if (this.$route.query.page) {
        this.pagination.page = this.$route.query.page
      }
      if (this.$route.query.rowsPerPage) {
        this.pagination.rowsPerPage = this.$route.query.rowsPerPage
      }
      if (this.$route.query.sortBy) {
        this.pagination.sortBy = this.$route.query.sortBy
      }
      if (this.$route.query.searchPhone) {
        this.searchPhone = this.$route.query.searchPhone
      }
      if (this.$route.query.dateFrom) {
        this.dateFrom = this.$route.query.dateFrom
      }
      if (this.$route.query.dateTo) {
        this.dateTo = this.$route.query.dateTo
      }
      if (this.$route.query.selectedVendor) {
        this.selectedVendor = this.$route.query.selectedVendor
      }
      if (this.$route.query.selectedLogTypes) {
        const selectedLogTypes = Array.isArray(this.$route.query.selectedLogTypes) ? this.$route.query.selectedLogTypes : [this.$route.query.selectedLogTypes]
        this.selectedLogTypes = selectedLogTypes.filter((logType) => logType)
      }
    },
    async initCountryCurrency () {
      let vendor = this.vendors.find((o) => o.value === this.selectedVendor)

      if (!vendor) {
        vendor = this.vendors[0]
        if (this.isVendor) {
          this.selectedVendor = vendor.value
        }
      }

      this.countryCurrencies = await this.$store.getters.getCountryCurrencies
      this.countryCurrency = this.countryCurrencies[vendor.metadata.countryId]
    },
    onPagination () {
      if (this.componentMounted) {
        this.searchTransactions()
      }
    },
    goToPreviousPage () {
      if (!this.canGoToPreviousPage) return
      this.pagination.page = Number(this.pagination.page) - 1
      this.searchTransactions()
    },
    goToNextPage () {
      if (!this.canGoToNextPage) return
      this.pagination.page = Number(this.pagination.page) + 1
      this.searchTransactions()
    },
    async viewMetadata (item) {
      this.metadataLoaded = true
      this.metadataTab = 'details'
      this.expireNowRefundUnusedBalance = false
      this.resetTransactionTripState()
      this.selectedMetadata = { ...item }

      if (this.isSelectedPurchasedTicket || (this.selectedMetadata && this.selectedMetadata.logMetadata && this.selectedMetadata.logMetadata.transportationProfileEventId)) {
        await this.loadTransactionTrips(this.selectedMetadata)
      }
    },
    async loadTransactionTrips (transaction) {
      this.transactionTripsLoading = true
      this.transactionTripsError = ''

      try {
        const trips = await findPassengerTrips({
          join: {
            relation: 'passengerTransactionLogTrips',
            scope: { where: { passengerTransactionLogId: transaction.id } }
          },
          include: 'transportationProfile',
          order: 'dateCreated ASC'
        })

        this.transactionTrips = await this.hydrateTripRouteLabels((trips || []).map((trip, index) => this.marshallPassengerTrip(trip, index)))

        if (this.transactionTrips.length) {
          this.selectedTripId = this.transactionTrips[0].id
          this.hydrateTripDetailsMetadata()
        }
      } catch (err) {
        this.transactionTripsError = err && err.message ? err.message : String(err)
      } finally {
        this.transactionTripsLoading = false
      }
    },
    marshallPassengerTrip (trip, index) {
      return {
        ...trip,
        tripOptionLabel: formatTripOptionLabel(trip, index),
        licencePlateNumber: getTripLicencePlate(trip),
        dateEmbarkedLabel: trip.dateEmbarked ? `${formatTime(trip.dateEmbarked)}, ${formatDate(trip.dateEmbarked)}` : '',
        dateDisembarkedLabel: trip.dateDisembarked ? `${formatTime(trip.dateDisembarked)}, ${formatDate(trip.dateDisembarked)}` : ''
      }
    },
    collectUniqueRouteIdLists (trips) {
      const routeIdLists = []
      for (let i = 0; i < trips.length; i++) {
        const routeIdList = trips[i].transportationRouteIdList
        if (routeIdList && routeIdLists.indexOf(routeIdList) === -1) {
          routeIdLists.push(routeIdList)
        }
      }
      return routeIdLists
    },
    async lookupStRouteLabel (routeIdList) {
      if (!routeIdList) return ''
      try {
        const routes = await findStRoutes({ routeIdList: routeIdList, limit: 1 })
        if (routes && routes[0] && routes[0].label) return routes[0].label
      } catch (err) {
        return ''
      }
      return ''
    },
    async hydrateTripRouteLabels (trips) {
      const routeIdLists = this.collectUniqueRouteIdLists(trips)
      const lookedUpLabels = await Promise.all(routeIdLists.map((routeIdList) => this.lookupStRouteLabel(routeIdList)))
      const labelsByRouteIdList = {}
      for (let i = 0; i < routeIdLists.length; i++) {
        labelsByRouteIdList[routeIdLists[i]] = lookedUpLabels[i]
      }

      return trips.map((trip) => {
        return Object.assign({}, trip, {
          routeLabel: formatTripRouteLabel(trip, labelsByRouteIdList[trip.transportationRouteIdList])
        })
      })
    },
    hydrateTripDetailsMetadata () {
      const cachedEventTrip = this.transactionTrips.find((trip) => trip.cachedTransportationProfileEvent)

      if (cachedEventTrip && cachedEventTrip.cachedTransportationProfileEvent) {
        const cachedTransportationProfileEvent = { ...cachedEventTrip.cachedTransportationProfileEvent }
        cachedTransportationProfileEvent.flatRateFee = cachedTransportationProfileEvent.flatRateFee ? formatCurrency(cachedTransportationProfileEvent.flatRateFee) : cachedTransportationProfileEvent.flatRateFee
        cachedTransportationProfileEvent.pickupTime = cachedTransportationProfileEvent.pickupTime ? formatTime(cachedTransportationProfileEvent.pickupTime) : cachedTransportationProfileEvent.pickupTime
        cachedTransportationProfileEvent.eventDateLabel = cachedTransportationProfileEvent.eventDate ? formatDate(cachedTransportationProfileEvent.eventDate) : ''
        this.selectedMetadata = { ...this.selectedMetadata, cachedTransportationProfileEvent }
      }
    },
    resetTransactionTripState () {
      this.tripMapActivated = false
      this.destroyTripMapState()
      this.transactionTrips = []
      this.transactionTripsLoading = false
      this.transactionTripsError = ''
      this.selectedTripId = null
      this.tripBusStops = []
      this.tripBusStopsLoading = false
    },
    async expireTicketNow () {
      if (this.isOnlyInvestor) {
        return addAlert({ message: 'Stakeholders have read-only access to passenger transactions', type: 'warning', transient: true })
      }

      if (!this.isAdmin) {
        return addAlert({ message: 'Only admins can expire tickets', type: 'error', isModal: true })
      }

      if (!this.selectedMetadata || !this.selectedMetadata.id) {
        return addAlert({ message: 'No transaction selected', type: 'error', isModal: true })
      }

      if (this.selectedMetadata.logType !== 'purchased_ticket') {
        return addAlert({ message: 'Only purchased tickets can be expired', type: 'error', isModal: true })
      }

      if (this.selectedMetadata.logStatus === 'completed') {
        return addAlert({ message: 'Ticket is completed and cannot be expired', type: 'error', isModal: true })
      }

      let refundText = this.expireNowRefundUnusedBalance ? 'and refund remaining refundable amount' : 'without refund'
      if (this.expireNowRefundUnusedBalance && getTicketSurchargeAmount(this.selectedMetadata)) {
        refundText += ' (including the beacon maintenance fee)'
      }
      if (!confirm(`Expire this ticket now ${refundText}?`)) {
        return
      }

      this.apiInProgress = true

      try {
        const result = await expireTicketNow({
          id: this.selectedMetadata.id,
          issueUnusedBalanceRefund: this.expireNowRefundUnusedBalance
        })

        // Refresh list + reflect latest status in modal
        await this.searchTransactions()

        if (this.selectedMetadata) {
          this.selectedMetadata = { ...this.selectedMetadata, logStatus: 'expired' }
        }

        const refunded = result && typeof result.refundedAmount === 'number' ? result.refundedAmount : 0
        const refundId = result && result.refundTransactionLogId ? result.refundTransactionLogId : null

        addAlert({
          message: refundId ? `Ticket expired. Refund created: ${getShortTransactionLogId(refundId)}${refunded ? ' (amount: ' + formatCurrency(refunded) + ')' : ''}` : `Ticket expired.${refunded ? ' Refunded amount: ' + formatCurrency(refunded) + ')' : ''}`,
          type: 'success',
          isModal: true
        })

        this.metadataLoaded = false
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.apiInProgress = false
      }
    },
    async confirmRefundPurchasedBeacon () {
      if (this.isOnlyInvestor) {
        return addAlert({ message: 'Stakeholders have read-only access to passenger transactions', type: 'warning', transient: true })
      }

      if (!this.isAdmin) {
        return addAlert({ message: 'Only admins can refund a purchased beacon', type: 'error', isModal: true })
      }

      if (!this.canRefundPurchasedBeacon || !this.selectedMetadata || !this.selectedMetadata.id) {
        return addAlert({ message: 'This purchased beacon cannot be refunded', type: 'error', isModal: true })
      }

      const identifierKey = this.selectedPurchasedBeaconIdentifierKey || 'unknown beacon'
      const amountLabel = this.selectedPurchasedBeaconRefundAmountLabel || formatCurrency(Math.abs(this.selectedMetadata.amount || 0))
      if (!confirm(`Refund purchased beacon ${identifierKey} for ${amountLabel} to this passenger's wallet?`)) {
        return
      }

      this.apiInProgress = true

      try {
        const result = await refundPurchasedBeacon({ id: this.selectedMetadata.id })
        await this.searchTransactions()

        this.metadataLoaded = false

        addAlert({
          message: result && result.id
            ? `Purchased beacon refunded: ${getShortTransactionLogId(result.id)} (amount: ${formatCurrency(result.amount)})`
            : 'Purchased beacon refunded',
          type: 'success',
          isModal: true
        })
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.apiInProgress = false
      }
    },
    async viewUser (stUserId) {
      const user = await getUser({ id: stUserId })

      if (user && this.selectedMetadata) {
        this.selectedMetadata = { ...this.selectedMetadata, viewedUser: toViewedPerson(user) }
      }
    },
    async viewAgent () {
      try {
        const agentTransactionLogId = getAgentTopUpTransactionLogId(this.selectedMetadata)
        if (!agentTransactionLogId) {
          addAlert({ message: 'No agent found for this top-up', type: 'error', isModal: true })
          return
        }
        const stUser = getAgentStUserFromJoinedLog(await getAgentTransactionLog({ id: agentTransactionLogId }))
        if (!stUser) {
          addAlert({ message: 'No agent found for this top-up', type: 'error', isModal: true })
          return
        }
        this.selectedMetadata = {
          ...this.selectedMetadata,
          viewedAgent: toViewedPerson(stUser)
        }
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      }
    },
    getTicketPassengerProfileId (ticket) {
      if (!ticket) return null
      if (ticket.passengerProfileId) return ticket.passengerProfileId
      if (ticket.passengerIdentifier && ticket.passengerIdentifier.passengerProfileId) {
        return ticket.passengerIdentifier.passengerProfileId
      }
      return null
    },
    resolveUnusedBalanceRefundPassenger (selectedTickets) {
      if (!selectedTickets || !selectedTickets.length) {
        return { error: 'Select at least one purchased ticket to associate with the refund' }
      }

      const profileIds = selectedTickets.map((ticket) => this.getTicketPassengerProfileId(ticket))
      if (profileIds.some((id) => !id)) {
        return { error: 'One or more selected tickets are missing a passenger profile' }
      }

      const uniqueProfileIds = profileIds.filter((id, index) => profileIds.indexOf(id) === index)
      if (uniqueProfileIds.length !== 1) {
        return { error: 'Selected tickets must belong to the same passenger profile' }
      }

      return { passengerProfileId: uniqueProfileIds[0] }
    },
    openUnusedBalanceRefundDialog () {
      if (this.isOnlyInvestor) {
        return addAlert({ message: 'Stakeholders have read-only access to passenger transactions', type: 'warning', transient: true })
      }

      const resolved = this.resolveUnusedBalanceRefundPassenger(this.selectedPurchasedTickets)
      if (resolved.error) {
        return addAlert({ message: resolved.error, type: 'error', isModal: true })
      }

      this.newUnusedBalanceRefund = {
        passengerProfileId: resolved.passengerProfileId
      }
      this.createUnusedBalanceRefundDialog = true
    },
    async saveAgentCreditRefund ({
      newAgentCreditRefund
    }) {
      if (this.isOnlyInvestor) {
        return addAlert({ message: 'Stakeholders have read-only access to passenger transactions', type: 'warning', transient: true })
      }

      if (!confirm('Are you sure you want to refund this agent credit?')) {
        return
      }

      this.apiInProgress = true

      try {
        await createAgentCreditRefund({
          passengerProfileId: newAgentCreditRefund.passengerIdentifier.passengerProfileId,
          amount: newAgentCreditRefund.amount,
          agentProfileId: newAgentCreditRefund.agent.id
        })

        this.createAgentCreditRefundDialog = false
        this.newAgentCreditRefund = {}
        this.searchTransactions()

        addAlert({
          message: `Agent credit refund saved successfully`,
          type: 'success'
        })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error',
          isModal: true
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async saveUnusedBalanceRefund ({
      selectedPurchasedTickets,
      newUnusedBalanceRefund
    }) {
      if (this.isOnlyInvestor) {
        return addAlert({ message: 'Stakeholders have read-only access to passenger transactions', type: 'warning', transient: true })
      }

      const resolved = this.resolveUnusedBalanceRefundPassenger(selectedPurchasedTickets)
      if (resolved.error) {
        return addAlert({ message: resolved.error, type: 'error', isModal: true })
      }

      if (!confirm('Are you sure you want to create this unused balance refund?')) {
        return
      }

      this.apiInProgress = true
      let dateCreatedInAccra = null

      if (newUnusedBalanceRefund.dateCreated) {
        // set the dateCreated to 13:00:00 of the timezone "Africa/Accra" but make sure the date is the same, just in the specific timezone
        const dateCreatedAsMoment = moment.utc(newUnusedBalanceRefund.dateCreated)
        const timezone = 'Africa/Accra'
        const dateCreatedAsMomentInAccra = dateCreatedAsMoment.tz(timezone)
        dateCreatedInAccra = dateCreatedAsMomentInAccra.set('hour', 13).set('minute', 0).set('second', 0).toDate()
      }

      try {
        const refund = await createUnusedBalanceRefund({
          passengerProfileId: resolved.passengerProfileId,
          amount: newUnusedBalanceRefund.amount,
          refundFromTransactionLogIds: selectedPurchasedTickets.map((o) => o.id),
          dateCreated: dateCreatedInAccra ? dateCreatedInAccra.toISOString() : undefined
        })
        const refundedAmount = refund && refund.amount ? refund.amount : newUnusedBalanceRefund.amount
        const includedFee = refund && refund.logMetadata && refund.logMetadata.includesSurcharges

        this.createUnusedBalanceRefundDialog = false
        this.newUnusedBalanceRefund = {}
        this.searchTransactions()

        addAlert({
          message: `Unused balance refund saved successfully. Refunded ${this.formatSurchargeAmount(refundedAmount)}${includedFee ? ' (includes the beacon maintenance fee)' : ''}`,
          type: 'success'
        })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error',
          isModal: true
        })
      } finally {
        this.apiInProgress = false
      }
    },
    submitSearch () {
      this.lastParams = addParamsToLocation({ dateFrom: this.dateFrom, dateTo: this.dateTo, searchPhone: this.searchPhone, selectedLogTypes: this.selectedLogTypes }, this.$route, this.lastParams)
      this.pagination.page = 1
      if (this.componentMounted) {
        this.searchTransactions()
      }
    },
    getTripBusStopErrorMessage (err) {
      if (err && err.error && err.error.message) return err.error.message
      if (err && err.message) return err.message
      return JSON.stringify(err) || 'Unknown error'
    },
    destroyTripMapState () {
      if (this.tripMapPopups && this.tripMapPopups.length) {
        this.tripMapPopups.forEach((popup) => popup && popup.remove && popup.remove())
      }
      this.tripMapPopups = []
      this.tripMapboxInstanceWrapper = null
    },
    activateTripMap () {
      // Measure the height offset before the map mounts so its canvas is created
      // at its final size. Resizing the canvas after the first paint smears the
      // route/line layers, which is the artifact we are avoiding here.
      this.$nextTick(() => {
        if (this.metadataTab !== 'trips' || !this.metadataLoaded) return
        this.updateTripMapHeightOffset()
        this.tripMapActivated = true
        this.loadSelectedTripBusStops()
      })
    },
    async loadSelectedTripBusStops () {
      this.tripBusStops = []

      const routeIdList = getTripBusStopRouteIdList(this.selectedTrip)
      if (!routeIdList) {
        this.applyTripBusStops()
        return
      }

      this.tripBusStopsLoading = true

      try {
        this.tripBusStops = await getBusStopsByRouteIdList(routeIdList)
      } catch (err) {
        addAlert({
          message: `Error loading trip bus stops: ${this.getTripBusStopErrorMessage(err)}`,
          type: 'warning',
          transient: true
        })
      } finally {
        this.tripBusStopsLoading = false
        this.applyTripBusStops()
      }
    },
    onTripMapLoaded (mapboxInstance) {
      this.tripMapboxInstanceWrapper = Object.freeze({ mapboxInstance })
      this.settleAndRenderTripMap()
    },
    settleAndRenderTripMap () {
      const tripMapboxInstance = this.getTripMapboxInstance()
      const currentMap = tripMapboxInstance && tripMapboxInstance.currentMap
      if (!currentMap) {
        this.renderTripMapOverlay()
        return
      }

      // A map created before its container is laid out (e.g. when the maptiler style is
      // served from cache on a reopen) renders blank tiles. Wait for layout, force one
      // resize so the GL canvas paints, then draw the overlay with a non-animated fit.
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          if (this.getTripMapboxInstance() !== tripMapboxInstance) return
          if (typeof currentMap.resize === 'function') {
            currentMap.resize()
          }
          this.renderTripMapOverlay()
        })
      })
    },
    getTripMapboxInstance () {
      return this.tripMapboxInstanceWrapper ? this.tripMapboxInstanceWrapper.mapboxInstance : null
    },
    getRefElement (refName) {
      const ref = this.$refs[refName]
      if (!ref) return null
      return ref.$el || ref
    },
    getRefHeight (refName, fallback) {
      const element = this.getRefElement(refName)
      if (!element) return fallback
      return element.clientHeight || fallback
    },
    updateTripMapHeightOffset () {
      const viewportReserve = Math.ceil(window.innerHeight * 0.1)
      const titleHeight = this.getRefHeight('metadataDialogTitle', 64)
      const tabsHeight = this.getRefHeight('metadataDialogTabs', 48)
      const footerHeight = this.getRefHeight('metadataDialogFooter', 0)
      // Cards sit beside the map, so only chrome above/below the trips layout is reserved.
      this.tripMapHeightOffset = viewportReserve + titleHeight + tabsHeight + footerHeight + 56
    },
    applyTripBusStops () {
      const tripMapboxInstance = this.getTripMapboxInstance()
      if (!tripMapboxInstance || !tripMapboxInstance.updateBusStops) return
      tripMapboxInstance.updateBusStops(toMapboxBusStops(this.tripBusStops), TRIP_BUS_STOPS_MIN_ZOOM)
    },
    geoPointToLngLatPair (point) {
      return point ? [point.lng, point.lat] : null
    },
    renderTripMapOverlay () {
      const tripMapboxInstance = this.getTripMapboxInstance()
      if (!tripMapboxInstance || !this.selectedTrip) return

      this.clearTripMapOverlay()
      this.applyTripBusStops()

      const boardPoint = normalizeGeoPoint(this.selectedTrip.longlatEmbarked)
      const alightPoint = normalizeGeoPoint(this.selectedTrip.longlatDisembarked)

      fitMapBounds(tripMapboxInstance, [
        this.selectedTripRouteCoordinates,
        this.geoPointToLngLatPair(boardPoint),
        this.geoPointToLngLatPair(alightPoint)
      ])

      this.renderTripEndpoints(boardPoint, alightPoint)
    },
    renderTripEndpoints (boardPoint, alightPoint) {
      const tripMapboxInstance = this.getTripMapboxInstance()
      const currentMap = tripMapboxInstance && tripMapboxInstance.currentMap
      if (!currentMap) return

      const data = { type: 'FeatureCollection', features: this.buildTripEndpointFeatures(boardPoint, alightPoint) }
      const existingSource = currentMap.getSource(TRIP_ENDPOINTS_SOURCE_ID)
      if (existingSource) {
        existingSource.setData(data)
        return
      }

      // Board/alight pins are drawn as an in-canvas symbol layer (the route line and bus
      // stops render the same way) instead of DOM markers. DOM markers overlaid on the
      // WebGL canvas smear inside this transformed dialog container; canvas layers do not.
      // The pin icons must be registered before the layer that references them, so add the
      // layer only after the images resolve; fall back to plain dots if they fail to load.
      currentMap.addSource(TRIP_ENDPOINTS_SOURCE_ID, { type: 'geojson', data })
      this.ensureTripEndpointIcons(tripMapboxInstance)
        .then(() => this.addTripEndpointSymbolLayer(tripMapboxInstance))
        .catch(() => this.addTripEndpointCircleLayer(tripMapboxInstance))
    },
    ensureTripEndpointIcons (tripMapboxInstance) {
      return Promise.all([
        tripMapboxInstance.addLayerImage(TRIP_BOARD_ICON_URL, TRIP_BOARD_ICON_ID),
        tripMapboxInstance.addLayerImage(TRIP_ALIGHT_ICON_URL, TRIP_ALIGHT_ICON_ID)
      ])
    },
    canAddTripEndpointLayer (tripMapboxInstance) {
      const currentMap = tripMapboxInstance && tripMapboxInstance.currentMap
      if (!currentMap || this.getTripMapboxInstance() !== tripMapboxInstance) return false
      return !!currentMap.getSource(TRIP_ENDPOINTS_SOURCE_ID) && !currentMap.getLayer(TRIP_ENDPOINTS_LAYER_ID)
    },
    addTripEndpointSymbolLayer (tripMapboxInstance) {
      if (!this.canAddTripEndpointLayer(tripMapboxInstance)) return

      const currentMap = tripMapboxInstance.currentMap
      currentMap.addLayer({
        id: TRIP_ENDPOINTS_LAYER_ID,
        type: 'symbol',
        source: TRIP_ENDPOINTS_SOURCE_ID,
        layout: {
          'icon-image': ['match', ['get', 'kind'], 'alight', TRIP_ALIGHT_ICON_ID, TRIP_BOARD_ICON_ID],
          'icon-size': TRIP_ENDPOINT_ICON_SIZE,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      })
      currentMap.on('click', TRIP_ENDPOINTS_LAYER_ID, this.onTripEndpointClick)
    },
    addTripEndpointCircleLayer (tripMapboxInstance) {
      if (!this.canAddTripEndpointLayer(tripMapboxInstance)) return

      const currentMap = tripMapboxInstance.currentMap
      currentMap.addLayer({
        id: TRIP_ENDPOINTS_LAYER_ID,
        type: 'circle',
        source: TRIP_ENDPOINTS_SOURCE_ID,
        paint: {
          'circle-radius': 9,
          'circle-color': ['match', ['get', 'kind'], 'alight', '#1e88e5', '#90caf9'],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        }
      })
      currentMap.on('click', TRIP_ENDPOINTS_LAYER_ID, this.onTripEndpointClick)
    },
    buildTripEndpointFeatures (boardPoint, alightPoint) {
      const features = []
      if (boardPoint) {
        features.push(this.buildTripEndpointFeature('board', boardPoint, this.getBoardPopupHtml()))
      }
      if (alightPoint) {
        features.push(this.buildTripEndpointFeature('alight', alightPoint, this.getAlightPopupHtml()))
      }
      return features
    },
    buildTripEndpointFeature (kind, point, popupHtml) {
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [point.lng, point.lat] },
        properties: { kind, popupHtml }
      }
    },
    getClickedFeature (event) {
      if (!event || !event.features || !event.features.length) return null
      return event.features[0]
    },
    onTripEndpointClick (event) {
      const tripMapboxInstance = this.getTripMapboxInstance()
      const currentMap = tripMapboxInstance && tripMapboxInstance.currentMap
      const feature = this.getClickedFeature(event)
      if (!currentMap || !feature) return

      const popup = tripMapboxInstance.createPopup({
        className: 'st-map--popup',
        closeButton: true,
        closeOnClick: true,
        offset: 12
      })
      popup.setLngLat(feature.geometry.coordinates).setHTML(feature.properties.popupHtml).addTo(currentMap)
      this.tripMapPopups.push(popup)
    },
    removeTripMapPopups () {
      if (this.tripMapPopups && this.tripMapPopups.length) {
        this.tripMapPopups.forEach((popup) => popup && popup.remove && popup.remove())
      }
      this.tripMapPopups = []
    },
    removeTripEndpointsLayer (currentMap) {
      if (currentMap.getLayer(TRIP_ENDPOINTS_LAYER_ID)) {
        currentMap.off('click', TRIP_ENDPOINTS_LAYER_ID, this.onTripEndpointClick)
        currentMap.removeLayer(TRIP_ENDPOINTS_LAYER_ID)
      }
      if (currentMap.getSource(TRIP_ENDPOINTS_SOURCE_ID)) {
        currentMap.removeSource(TRIP_ENDPOINTS_SOURCE_ID)
      }
    },
    clearTripMapOverlay () {
      const tripMapboxInstance = this.getTripMapboxInstance()
      this.removeTripMapPopups()

      const currentMap = tripMapboxInstance && tripMapboxInstance.currentMap
      if (currentMap) {
        this.removeTripEndpointsLayer(currentMap)
      }

      if (tripMapboxInstance && tripMapboxInstance.updateBusStops) {
        tripMapboxInstance.updateBusStops([], TRIP_BUS_STOPS_MIN_ZOOM)
      }
    },
    getBoardPopupHtml () {
      return buildTripPopupHtml({
        title: 'Board',
        fields: [
          { label: 'Location', value: this.selectedTrip.originLabel },
          { label: 'Date/time', value: this.selectedTrip.dateEmbarkedLabel },
          { label: 'Route', value: this.selectedTripRouteLabel },
          { label: 'Bus licence plate', value: this.selectedTripLicencePlate || 'N/A' }
        ]
      })
    },
    getAlightPopupHtml () {
      return buildTripPopupHtml({
        title: 'Alight',
        fields: [
          { label: 'Location', value: this.selectedTrip.destinationLabel },
          { label: 'Date/time', value: this.selectedTrip.dateDisembarkedLabel },
          { label: 'Route', value: this.selectedTripRouteLabel },
          { label: 'Bus licence plate', value: this.selectedTripLicencePlate || 'N/A' },
          { label: 'Final fare', value: this.selectedTripFareLabel },
          { label: 'Distance travelled', value: this.selectedTripDistanceLabel }
        ]
      })
    },
    isOneTimeTicketTransaction (transaction) {
      if (!transaction || transaction.logType !== 'purchased_ticket') return false
      const logMetadata = transaction.logMetadata || {}
      if (logMetadata.oneTimeTicket) return true
      return logMetadata.quickPayType === 'oneTimeTicket'
    },
    getTransactionLogTypeLabel (transaction) {
      if (!transaction) return ''
      return this.isOneTimeTicketTransaction(transaction) ? 'one_time_ticket' : transaction.logType
    },
    marshallTransaction (o) {
      const amountClass = o.amount < 0 ? 'error--text' : ''
      const balanceBeforeClass = o.profileBalanceBefore < 0 ? 'error--text' : ''
      const balanceAfterClass = o.profileBalanceAfter < 0 ? 'error--text' : ''
      o.amountLabel = `<span class="${amountClass}">${this.countryCurrency.currencySymbol}${formatCurrency(o.amount)}</span>`
      o.profileBalanceBeforeLabel = `<span class="${balanceBeforeClass}">${formatCurrency(o.profileBalanceBefore)}</span>`
      o.profileBalanceAfterLabel = `<span class="${balanceAfterClass}">${formatCurrency(o.profileBalanceAfter)}</span>`
      o.phoneLabel = o.passengerIdentifier ? o.passengerIdentifier.phoneNumber : (o.stUser?.phone || '')
      o.parsedId = getShortTransactionLogId(o.id)
      o.logTypeLabel = this.getTransactionLogTypeLabel(o)
      o.isAgentTopUp = isAgentTopUpPassengerTransaction(o)

      if (o.logType === 'refund') {
        o.logTypeIcon = 'fas fa-undo'
      } else if (o.logType === 'unused_balance_refund') {
        o.logTypeIcon = 'fas fa-recycle'
      } else if (o.logType === 'purchased_gift') {
        o.logTypeIcon = 'fas fa-gift'
      } else if (o.logType === 'purchased_ticket') {
        o.logTypeIcon = 'fas fa-ticket-alt'
      } else if (o.logType === 'purchased_credit') {
        o.logTypeIcon = 'fas fa-money-bill-wave'
      } else if (o.logType === 'purchased_beacon') {
        o.logTypeIcon = 'fas fa-tag'
      } else if (o.logType === 'purchased_beacon_refund') {
        o.logTypeIcon = 'fas fa-undo'
      } else {
        o.logTypeIcon = ''
      }

      switch (o.logStatus) {
        case 'error':
          o.logStatusIcon = 'fas fa-exclamation-triangle'
          o.logStatusTooltip = 'Error: ' + o.logMetadata.error
          break
        case 'confirmed':
        case 'completed':
          o.logStatusIcon = 'fas fa-check'
          o.logStatusTooltip = o.logStatus === 'confirmed' ? 'Confirmed' : 'Completed'
          break
        case 'timedout':
          o.logStatusIcon = 'fas fa-hourglass-end'
          o.logStatusTooltip = 'Timeout occurred while waiting for vendor feedback'
          break
        case 'expired':
          o.logStatusIcon = 'fas fa-calendar-times'
          o.logStatusTooltip = 'Expired'
          break
        default:
          o.logStatusIcon = 'fas fa-info-circle'
          o.logStatusTooltip = o.logStatus
      }

      o.dateCreatedLabel = `${formatTime(o.dateCreated)}, ${formatDate(o.dateCreated)}`
      o.dateRecordedLabel = `${formatTime(o.dateRecorded)}, ${formatDate(o.dateRecorded)}`
      o.dateUpdatedLabel = o.dateUpdated ? `${formatTime(o.dateUpdated)}, ${formatDate(o.dateUpdated)}` : o.dateCreatedLabel
      o.showDateCreatedInTag = shouldShowDateRecorded(o)

      return o
    },
    async searchTransactions () {
      const requestId = (this.lastSearchRequestId || 0) + 1
      this.lastSearchRequestId = requestId

      const { sortBy, descending, page, rowsPerPage } = this.pagination
      const offset = page === 1 ? 0 : (page * rowsPerPage) - rowsPerPage

      let params = {
        vendor: null,
        filter: {
          limit: rowsPerPage,
          offset,
          where: { logType: { neq: 'ad_hoc_revenue' } }
        }
      }

      if (!sortBy) {
        params.filter.order = ['dateUpdated DESC', 'dateCreated DESC']
      } else {
        params.filter.order = `${sortBy} ${descending ? 'DESC' : 'ASC'}`
      }

      if (this.searchKeywords) {
        params.stUserKeywords = { or: generateKeywordsQueryForOr(this.searchKeywords, ['value']) }
      }

      if (this.selectedVendor) {
        params.vendor = this.selectedVendor
      }

      if (this.selectedLogTypes.length) {
        params.filter.where.logType = {
          inq: this.selectedLogTypes,
          neq: 'ad_hoc_revenue'
        }
      }

      if (this.searchPhone) {
        params.searchPhone = this.searchPhone
      }

      if (this.dateFrom) {
        if (!params.filter.where.and) {
          params.filter.where.and = []
        }

        params.filter.where.and.push({ dateCreated: { gte: moment.utc(this.dateFrom, 'Y-MM-DD').toDate().toISOString() } })
      }

      if (this.dateTo) {
        if (!params.filter.where.and) {
          params.filter.where.and = []
        }

        params.filter.where.and.push({ dateCreated: { lte: moment.utc(this.dateTo, 'Y-MM-DD').add(23, 'hours').add(59, 'minutes').toDate().toISOString() } })
      }

      this.apiInProgress = true
      this.countInProgress = true
      // Reset total early so list rendering never waits on count.
      this.totalTransactions = null

      try {
        let transactions

        if (params.vendor) {
          transactions = await findVendorTransactionLogs(params)

          // Fire-and-forget count. If it times out or errors, we keep totalTransactions as null.
          findTotalVendorTransactionLogs({
            where: params.filter.where,
            stUserKeywords: params.stUserKeywords,
            searchPhone: params.searchPhone,
            vendor: params.vendor,
            timeoutMsecs: 2000,
            returnNullOnTimeout: true
          }).then((result) => {
            if (this.lastSearchRequestId !== requestId) return
            this.totalTransactions = result && typeof result.count === 'number' ? result.count : null
          }).catch(() => {
            if (this.lastSearchRequestId !== requestId) return
            this.totalTransactions = null
          }).finally(() => {
            if (this.lastSearchRequestId !== requestId) return
            this.countInProgress = false
          })
        } else {
          if (!Object.keys(params.filter.where).length) {
            params.filter.where = null
          }

          transactions = await findGeneralVendorTransactionLogs(params)

          // Fire-and-forget count. If it times out or errors, we keep totalTransactions as null.
          findTotalGeneralVendorTransactionLogs({
            where: params.filter.where,
            searchPhone: params.searchPhone,
            timeoutMsecs: 2000,
            returnNullOnTimeout: true
          }).then((result) => {
            if (this.lastSearchRequestId !== requestId) return
            this.totalTransactions = result && typeof result.count === 'number' ? result.count : null
          }).catch(() => {
            if (this.lastSearchRequestId !== requestId) return
            this.totalTransactions = null
          }).finally(() => {
            if (this.lastSearchRequestId !== requestId) return
            this.countInProgress = false
          })
        }

        if (this.lastSearchRequestId !== requestId) {
          return
        }

        this.transactions = transactions.map(o => {
          return this.marshallTransaction(o)
        })

        this.apiInProgress = false
      } catch (err) {
        this.apiInProgress = false
        this.countInProgress = false

        if (err && !err.error) {
          addAlert({
            message: `Error in getting transactions: ${err && err.message ? err.message : JSON.stringify(err)}`,
            type: 'error'
          })
        }
      }
    }
  },
  beforeDestroy () {
    this.destroyTripMapState()
  }
}
